import React, { useState } from 'react';
import { DishPortion } from '../types/dish.types';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { dishService } from '../services/dish.service';
import { CURRENCY_SYMBOL } from '../config/currency';

interface PortionPricesTableProps {
  portions: DishPortion[];
  onPriceUpdate: () => void;
}

export const PortionPricesTable: React.FC<PortionPricesTableProps> = ({ portions, onPriceUpdate }) => {
  const { t, language } = useLanguage();
  const { success, error } = useNotification();
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState<string>('');
  const [updatingPrices, setUpdatingPrices] = useState<Set<number>>(new Set());

  // Получаем уникальные источники из всех граммовок
  const sources = portions.length > 0 && portions[0].prices.length > 0
    ? portions[0].prices.map(p => p.source).sort((a, b) => a.id - b.id)
    : [];

  const handlePriceEdit = (priceId: number, currentPrice: number) => {
    setEditingPriceId(priceId);
    setEditingPrice(currentPrice.toString());
  };

  const handlePriceSave = async (priceId: number) => {
    const newPrice = parseFloat(editingPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      error(t('portionPrices.errorValidation'), t('portionPrices.errorInvalidPrice'));
      return;
    }

    setUpdatingPrices(prev => new Set(prev).add(priceId));
    try {
      await dishService.updatePrice(priceId, { price: newPrice });
      success(t('portionPrices.success'), t('portionPrices.successPriceUpdated'));
      setEditingPriceId(null);
      onPriceUpdate();
    } catch (err) {
      error(t('portionPrices.error'), t('portionPrices.errorUpdateFailed'));
    } finally {
      setUpdatingPrices(prev => {
        const newSet = new Set(prev);
        newSet.delete(priceId);
        return newSet;
      });
    }
  };

  const handleVisibilityToggle = async (priceId: number, currentVisibility: boolean) => {
    setUpdatingPrices(prev => new Set(prev).add(priceId));
    try {
      await dishService.updatePrice(priceId, { is_visible: !currentVisibility });
      success(t('portionPrices.success'), t('portionPrices.successVisibilityUpdated'));
      onPriceUpdate();
    } catch (err) {
      error(t('portionPrices.error'), t('portionPrices.errorUpdateFailed'));
    } finally {
      setUpdatingPrices(prev => {
        const newSet = new Set(prev);
        newSet.delete(priceId);
        return newSet;
      });
    }
  };

  const handlePromoToggle = async (priceId: number, currentValue: boolean) => {
    setUpdatingPrices(prev => new Set(prev).add(priceId));
    try {
      await dishService.updatePrice(priceId, { allow_promo: !currentValue });
      success(t('portionPrices.success'), t('portionPrices.successPromoUpdated'));
      onPriceUpdate();
    } catch (err) {
      error(t('portionPrices.error'), t('portionPrices.errorUpdateFailed'));
    } finally {
      setUpdatingPrices(prev => {
        const newSet = new Set(prev);
        newSet.delete(priceId);
        return newSet;
      });
    }
  };

  const handleDiscountToggle = async (priceId: number, currentValue: boolean) => {
    setUpdatingPrices(prev => new Set(prev).add(priceId));
    try {
      await dishService.updatePrice(priceId, { allow_item_discount: !currentValue });
      success(t('portionPrices.success'), t('portionPrices.successDiscountUpdated'));
      onPriceUpdate();
    } catch (err) {
      error(t('portionPrices.error'), t('portionPrices.errorUpdateFailed'));
    } finally {
      setUpdatingPrices(prev => {
        const newSet = new Set(prev);
        newSet.delete(priceId);
        return newSet;
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingPriceId(null);
    setEditingPrice('');
  };

  if (portions.length === 0) {
    return (
      <div className="text-center py-8 bg-background-100 rounded-lg border-2 border-dashed border-background-300">
        <p className="text-text-tertiary">{t('portionPrices.noPortions')}</p>
      </div>
    );
  }

  if (sources.length === 0) {
    return (
      <div className="text-center py-8 bg-background-100 rounded-lg border-2 border-dashed border-background-300">
        <p className="text-text-tertiary">{t('portionPrices.noSources')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-background-300">
            <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary bg-background-100">
              {t('portionPrices.columnPortion')}
            </th>
            {sources.map((source) => (
              <th
                key={source.id}
                className="px-4 py-3 text-center text-sm font-semibold text-text-primary bg-background-100"
              >
                {language === 'ru' ? source.name_ru : source.name_en}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {portions
            .filter((p) => !p.is_deleted)
            .sort((a, b) => a.weight - b.weight)
            .map((portion) => (
              <tr key={portion.id} className="border-b border-background-200 hover:bg-background-50">
                <td className="px-4 py-4 font-medium text-text-primary">
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-bold text-primary-600">{portion.weight}</span>
                    <span className="text-sm text-text-secondary">{t('portionPrices.grams')}</span>
                  </div>
                </td>
                {sources.map((source) => {
                  const price = portion.prices.find((p) => p.source_id === source.id);
                  if (!price) return <td key={source.id} className="px-4 py-4 text-center">-</td>;

                  const isEditing = editingPriceId === price.id;
                  const isUpdating = updatingPrices.has(price.id);

                  return (
                    <td key={price.id} className="px-4 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Видимость и цена */}
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleVisibilityToggle(price.id, price.is_visible)}
                            disabled={isUpdating}
                            className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-colors ${
                              price.is_visible
                                ? 'bg-success-100 text-success-700 hover:bg-success-200'
                                : 'bg-background-200 text-text-tertiary hover:bg-background-300'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={price.is_visible ? t('portionPrices.visible') : t('portionPrices.hidden')}
                          >
                            {price.is_visible ? '✓' : '☐'}
                          </button>

                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editingPrice}
                                onChange={(e) => setEditingPrice(e.target.value)}
                                className="w-20 px-2 py-1 text-sm border border-primary-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                                step="0.01"
                                min="0"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handlePriceSave(price.id);
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                              />
                              <button
                                onClick={() => handlePriceSave(price.id)}
                                disabled={isUpdating}
                                className="text-success-600 hover:text-success-700 disabled:opacity-50"
                                title={t('portionPrices.save')}
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={isUpdating}
                                className="text-error-600 hover:text-error-700 disabled:opacity-50"
                                title={t('portionPrices.cancel')}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handlePriceEdit(price.id, Number(price.price))}
                              disabled={isUpdating || !price.is_visible}
                              className={`text-sm font-medium ${
                                price.is_visible
                                  ? 'text-text-primary hover:text-primary-600'
                                  : 'text-text-tertiary line-through'
                              } disabled:cursor-not-allowed transition-colors`}
                            >
                              {Number(price.price).toFixed(2)} {CURRENCY_SYMBOL}
                            </button>
                          )}
                        </div>

                        {/* Флаги - кликабельные переключатели */}
                        <div className="flex items-center justify-center gap-2 text-xs">
                          <button
                            onClick={() => handlePromoToggle(price.id, price.allow_promo)}
                            disabled={isUpdating}
                            className={`px-2 py-0.5 rounded transition-all cursor-pointer hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${
                              price.allow_promo
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                : 'bg-background-200 text-text-tertiary hover:bg-background-300'
                            }`}
                            title={`${t('portionPrices.allowPromo')} - ${t('portionPrices.clickToToggle')}`}
                          >
                            {t('portionPrices.promoLabel')}: {price.allow_promo ? '✓' : '✕'}
                          </button>
                          <button
                            onClick={() => handleDiscountToggle(price.id, price.allow_item_discount)}
                            disabled={isUpdating || source.code === 'glovo' || source.code === 'wolt'}
                            className={`px-2 py-0.5 rounded transition-all ${
                              source.code === 'glovo' || source.code === 'wolt'
                                ? 'cursor-not-allowed opacity-75'
                                : 'cursor-pointer hover:opacity-80'
                            } disabled:opacity-50 ${
                              price.allow_item_discount
                                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                : 'bg-background-200 text-text-tertiary hover:bg-background-300'
                            }`}
                            title={
                              source.code === 'glovo' || source.code === 'wolt'
                                ? t('portionPrices.allowDiscount') + ' (всегда включено)'
                                : `${t('portionPrices.allowDiscount')} - ${t('portionPrices.clickToToggle')}`
                            }
                          >
                            {t('portionPrices.discountLabel')}: {price.allow_item_discount ? '✓' : '✕'}
                          </button>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};
